import { LitElement, html, css } from 'lit-element';
import { PERSONALITIES, initialDivision } from './personalities.js';

class AgentPane extends LitElement {
  static get properties() {
    return {
      personality: { type: String },
      division: { type: Number },
      phase: { type: Number },
      hue: { type: Number },
      intensity: { type: Number },
      index: { type: Number },
    };
  }

  constructor() {
    super();
    this.personality = 'anchor';
    this.division = 12;
    this.phase = 0;
    this.hue = 220;
    this.intensity = 0;
    this.index = 0;
    this.tick = 0;
  }

  initFromIndex(i) {
    this.index = i;
    this.division = initialDivision(i);
  }

  static get styles() {
    return css`
      :host {
        display: block;
        position: relative;
        width: 100%;
        height: 100%;
        overflow: hidden;
        background: #050816;
      }
      .bars {
        position: absolute;
        inset: 0;
        display: flex;
      }
      .bar {
        flex: 1 1 0;
        height: 100%;
        background: currentColor;
        opacity: 0.08;
        transition: opacity 90ms ease-out, transform 90ms ease-out;
        transform-origin: center;
      }
      .bar.lit {
        opacity: 0.95;
        transform: scaleY(1.0);
      }
      .label {
        position: absolute;
        bottom: 4px;
        left: 6px;
        font: 10px/1 ui-monospace, Menlo, monospace;
        color: rgba(255, 255, 255, 0.55);
        mix-blend-mode: screen;
        pointer-events: none;
        letter-spacing: 0.5px;
      }
    `;
  }

  // The grid calls this per global tick. It returns the new state (no render
  // yet — grid batches a requestUpdate after all panes have stepped).
  step(neighbors) {
    this.tick += 1;
    const fn = PERSONALITIES[this.personality] || PERSONALITIES.anchor;
    const state = {
      division: this.division,
      phase: this.phase,
      hue: this.hue,
      intensity: this.intensity,
      tick: this.tick,
    };
    const next = fn(state, neighbors || []);
    if (typeof next.division === 'number') this.division = next.division;
    if (typeof next.hue === 'number') this.hue = next.hue;
    // phase advances 1 per global tick, modulo division
    this.phase = this.tick % this.division;
    // intensity = how brightly we fired this tick (downbeat = brightest)
    if (this.phase === 0) this.intensity = 1;
    else this.intensity = Math.max(0, this.intensity * 0.78);
  }

  snapshot() {
    return {
      division: this.division,
      phase: this.phase,
      hue: this.hue,
      intensity: this.intensity,
    };
  }

  render() {
    const bars = [];
    for (let i = 0; i < this.division; i += 1) {
      const lit = i === this.phase;
      const energy = lit ? 1 : 0;
      const style = `color: hsl(${this.hue}, 75%, ${30 + energy * 50}%); ${
        lit ? `opacity:${0.55 + this.intensity * 0.45};` : ''
      }`;
      bars.push(html`<div class="bar ${lit ? 'lit' : ''}" style=${style}></div>`);
    }
    return html`
      <div class="bars">${bars}</div>
      <div class="label">${this.personality}·${this.division}</div>
    `;
  }
}

customElements.define('agent-pane', AgentPane);
